import { HttpParams } from '@angular/common/http';
import { Params } from '@angular/router';
import { NormalizedStatus } from './api.models';

/** Filtres communs à la liste, à l'export et au tableau de bord (reflétés dans l'URL). */
export interface DocumentFilters {
  companyId: string[];
  status: NormalizedStatus[];
  /** Statuts bruts Flowie de la société (colonne « Statut Flowie »). */
  statusRaw: string[];
  issueDateFrom: string | null;
  issueDateTo: string | null;
  /** Date de création chez Flowie (réception), bornes incluses. */
  createdFrom: string | null;
  createdTo: string | null;
  /** Rôle de la société : émettrice (factures émises) ou destinataire (factures reçues). */
  role: '' | 'sender' | 'receiver';
  search: string;
  /** Société émettrice : partie du nom ou du n° de TVA. */
  sender: string;
  /** Société destinataire : partie du nom ou du n° de TVA. */
  receiver: string;
  amountMin: number | null;
  amountMax: number | null;
}

export const EMPTY_FILTERS: DocumentFilters = {
  companyId: [],
  status: [],
  statusRaw: [],
  issueDateFrom: null,
  issueDateTo: null,
  createdFrom: null,
  createdTo: null,
  role: '',
  search: '',
  sender: '',
  receiver: '',
  amountMin: null,
  amountMax: null,
};

const asArray = (value: unknown): string[] =>
  value === undefined || value === null || value === ''
    ? []
    : Array.isArray(value)
      ? value.map(String)
      : [String(value)];

const asNumber = (value: unknown): number | null => {
  const n = value === undefined || value === null || value === '' ? NaN : Number(value);
  return Number.isFinite(n) ? n : null;
};

const asDate = (value: unknown): string | null =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;

/** Lit les filtres depuis les paramètres de l'URL (valeurs invalides ignorées). */
export function filtersFromParams(params: Params): DocumentFilters {
  const role = params['role'];
  return {
    companyId: asArray(params['companyId']),
    status: asArray(params['status']) as NormalizedStatus[],
    statusRaw: asArray(params['statusRaw']),
    issueDateFrom: asDate(params['issueDateFrom']),
    issueDateTo: asDate(params['issueDateTo']),
    createdFrom: asDate(params['createdFrom']),
    createdTo: asDate(params['createdTo']),
    role: role === 'sender' || role === 'receiver' ? role : '',
    search: typeof params['search'] === 'string' ? params['search'] : '',
    sender: typeof params['sender'] === 'string' ? params['sender'] : '',
    receiver: typeof params['receiver'] === 'string' ? params['receiver'] : '',
    amountMin: asNumber(params['amountMin']),
    amountMax: asNumber(params['amountMax']),
  };
}

/** Paramètres d'URL (ou de requête) sans les valeurs vides. */
export function filtersToParams(filters: DocumentFilters): Params {
  const params: Params = {};
  if (filters.companyId.length) params['companyId'] = filters.companyId;
  if (filters.status.length) params['status'] = filters.status;
  if (filters.statusRaw.length) params['statusRaw'] = filters.statusRaw;
  if (filters.issueDateFrom) params['issueDateFrom'] = filters.issueDateFrom;
  if (filters.issueDateTo) params['issueDateTo'] = filters.issueDateTo;
  if (filters.createdFrom) params['createdFrom'] = filters.createdFrom;
  if (filters.createdTo) params['createdTo'] = filters.createdTo;
  if (filters.role) params['role'] = filters.role;
  if (filters.search.trim()) params['search'] = filters.search.trim();
  if (filters.sender.trim()) params['sender'] = filters.sender.trim();
  if (filters.receiver.trim()) params['receiver'] = filters.receiver.trim();
  if (filters.amountMin !== null) params['amountMin'] = filters.amountMin;
  if (filters.amountMax !== null) params['amountMax'] = filters.amountMax;
  return params;
}

export function toHttpParams(
  filters: DocumentFilters,
  extra: Record<string, string | number | boolean> = {},
): HttpParams {
  let params = new HttpParams();
  for (const [key, value] of Object.entries({ ...filtersToParams(filters), ...extra })) {
    for (const v of Array.isArray(value) ? value : [value]) {
      params = params.append(key, String(v));
    }
  }
  return params;
}

/** Date locale au format de l'API (AAAA-MM-JJ). */
export function toIsoDate(date: Date | null): string | null {
  if (!date) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function fromIsoDate(value: string | null): Date | null {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
}
